import {Composer, createI18n, I18n} from 'vue-i18n';
import merge from "lodash.merge";
import {localStoragePersistSafely} from "@/shared/lib/localStorage.ts";
import { useRoute, useRouter } from 'vue-router';

export class Locale {
    language: string;
    country: string;

    constructor(language: string, country: string) {
        this.language = language;
        this.country = country;
    }

    toString(): string {
        return `${this.language}-${this.country}`;
    }

    static fromJSON(jsonString: string): Locale {
        try {
            const obj = JSON.parse(jsonString);
            return new Locale(obj.language, obj.country);
        } catch (e) {
            return new Locale("nl", "BE");
        }
    }
    static fromString(inputString: string): Locale {
        try {
            if (!inputString.includes("-")) {
                return new Locale("nl", "BE");
            }
            const language = inputString.split("-")[0]
            const country = inputString.split("-")[1]
            return new Locale(language, country);
        } catch (e) {
            return new Locale("nl", "BE");
        }
    }
}

let supportedLocales: Locale[];
let i18n: I18n | null = null;
let appKey = 'default'

export const t = (key: string): string => (i18n.global as Composer).t(key)

export const activeLanguage = () => {
    const localeValue = (i18n.global as Composer).locale.value
    const activeLocale = supportedLocales.find((l) => l.toString() === localeValue.toString())
    if (activeLocale) {
        return activeLocale.language
    } else {
        return supportedLocales[0].language
    }
}

async function loadSharedMessages(locale: Locale) {
    const shared = await import(`./shared_${locale.language}.json`);
    return shared.default;
}

// Function to dynamically load project-specific translations
async function loadProjectMessages(locale: Locale) {
    const project = await import(`@@/i18n/${locale.language}.json`);
    return project.default;
}

async function loadLocaleMessages(locale: Locale) {
    const [sharedMessages, projectMessages] = await Promise.all([
        loadSharedMessages(locale),
        loadProjectMessages(locale),
    ]);
    return merge({}, sharedMessages, projectMessages);
}

// Initialize the i18n instance
async function initializeI18n(locales: Locale[], pAppKey:string): Promise<I18n> {

    appKey = pAppKey

    const lastLocaleString = localStorage.getItem(`${appKey}-locale`)
    const lastLocale = lastLocaleString ? Locale.fromJSON(lastLocaleString) : null;
    const browserLanguage = Locale.fromString(navigator.language).language
    let locale = lastLocale || locales.find(l => l.language === browserLanguage) || new Locale('nl', 'BE')

    supportedLocales = locales
    if (!supportedLocales.some(locale => locale.toString() === locale.toString())) {
        locale = supportedLocales[0]; // Fall back to this default locale
    }
    localStoragePersistSafely(`${appKey}-locale`, JSON.stringify(locale))

    // Load messages for the default locale
    const messages: Record<string, Record<string, any>> = {
        [locale.toString()]: await loadLocaleMessages(locale),
    };

    i18n = createI18n({
        legacy: false,
        locale: locale.toString(),
        fallbackLocale: locale.toString(),
        globalInjection: true,
        messages,
    }) as I18n;
    return i18n;
}

// Switch language
async function switchLocale(localeString: string, reload: boolean = true): Promise<void> {
    if (!i18n) throw new Error('i18n is not initialized');

    let locale = supportedLocales.find((l) => l.toString() === localeString);
    if (!locale) {
        console.error(`The locale ${localeString} is not supported`);
        locale = supportedLocales[0];
    }

    const composer = i18n.global as Composer;
    if (!composer.availableLocales.includes(locale.toString())) {
        // Load locale messages if not already loaded
        const messages = await loadLocaleMessages(locale);
        composer.setLocaleMessage(locale.toString(), messages);
    }

    localStoragePersistSafely(`${appKey}-locale`, JSON.stringify(locale));
    composer.locale.value = locale.toString();

    if (reload) {
        window.location.reload();
    }
}


export {initializeI18n, switchLocale, i18n, supportedLocales};